async function apiFetch(opts, path, init = {}) {
    const token = await opts.getToken();
    const headers = {
        "Content-Type": "application/json",
        ...init.headers,
    };
    if (token)
        headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${opts.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`Hub ${init.method ?? "GET"} ${path} failed: ${res.status} ${body}`);
    }
    if (res.status === 204)
        return undefined;
    const text = await res.text();
    if (!text)
        return undefined;
    return JSON.parse(text);
}
// Variant for endpoints that stream a binary body (vault export / scaffold
// zips, KTK-289). Uses the same auth + error envelope as `apiFetch` but
// returns the response Blob directly.
async function apiFetchBlob(opts, path, init = {}) {
    const token = await opts.getToken();
    const headers = {
        ...init.headers,
    };
    if (token)
        headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${opts.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`Hub ${init.method ?? "GET"} ${path} failed: ${res.status} ${body}`);
    }
    return res.blob();
}
/**
 * KTK-395 — reject a CLAUDE.md response that isn't the `/resolved` shape.
 *
 * The Settings panel white-screened in production because the workspace scope
 * was calling a per-layer route (`GET …/claude-md`, which answers
 * `{document: null}`) while the component expected the resolved document. The
 * first fix optional-chained the crash away, which converted a loud failure
 * into a panel that renders empty forever — including after someone authors a
 * layer, with nothing to say why.
 *
 * So: check here and throw with the path in the message. A shape violation is
 * a bug, not a state, and it should be impossible to mistake for "empty".
 */
function assertClaudeMdDocument(doc, path) {
    const d = doc;
    if (!d || !Array.isArray(d.layers) || typeof d.content !== "string") {
        throw new Error(`Hub ${path} did not return a composed CLAUDE.md document ` +
            `(expected \`layers\` array + \`content\` string, got ${JSON.stringify(doc)?.slice(0, 200)}). ` +
            `This is a contract mismatch, not an empty document.`);
    }
    return d;
}
/**
 * Browser-side HubClient — direct fetch to the Hub with a bearer token
 * resolved by the caller (Clerk JWT in practice). Works in both real
 * browsers and Electron renderers.
 */
export function createBrowserHubClient(options) {
    // KTK-395 — standalone rather than `this.getWorkspaceClaudeMd(...)` from a
    // sibling method. Nothing else in this client uses `this`, and a consumer
    // destructuring a method off the object (`const { setAgentClaudeMd } = hub`)
    // would get `undefined` and a TypeError at call time.
    const fetchWorkspaceClaudeMd = async (workspaceId) => {
        const path = `/api/workspaces/${workspaceId}/claude-md/resolved`;
        return assertClaudeMdDocument(await apiFetch(options, path), path);
    };
    const fetchAgentClaudeMd = async (workspaceId, agentId) => {
        const path = `/api/workspaces/${workspaceId}/agents/${agentId}/claude-md/resolved`;
        return assertClaudeMdDocument(await apiFetch(options, path), path);
    };
    return {
        // --- Workspaces ---
        async listWorkspaces() {
            const { workspaces } = await apiFetch(options, `/api/workspaces`);
            return workspaces;
        },
        async getMe() {
            try {
                // Backend returns `{ user: { id, email, active_workspace_id, ... } }`.
                // Unwrap to the flat shape HubClient.getMe advertises.
                const res = await apiFetch(options, `/api/me`);
                return res?.user ?? null;
            }
            catch {
                return null;
            }
        },
        async setActiveWorkspace(workspaceId) {
            await apiFetch(options, `/api/me/active-workspace`, {
                method: "POST",
                body: JSON.stringify({ workspace_id: workspaceId }),
            });
        },
        async bootstrapUser(name) {
            // KTK-335 — POST /api/user/me/bootstrap (neon). Empty body is the
            // happy first-run path; the server defaults the workspace name and
            // sets active_workspace_id itself, so no follow-up PATCH is needed.
            return apiFetch(options, `/api/user/me/bootstrap`, {
                method: "POST",
                body: JSON.stringify(name ? { name } : {}),
            });
        },
        async getWorkspaceSetting(workspaceId, key) {
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/settings/${encodeURIComponent(key)}`);
            return res?.value ?? null;
        },
        async setWorkspaceSetting(workspaceId, key, value) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/settings/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) });
        },
        async getMemberSetting(workspaceId, key) {
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/member-settings/${encodeURIComponent(key)}`);
            return res?.value ?? null;
        },
        async setMemberSetting(workspaceId, key, value) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/member-settings/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) });
        },
        // --- Channels ---
        async listChannels(workspaceId) {
            const { channels } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels`);
            return channels;
        },
        async createChannel(input) {
            const { channel } = await apiFetch(options, `/api/workspaces/${input.workspace_id}/channels`, {
                method: "POST",
                body: JSON.stringify({
                    name: input.name,
                    description: input.description,
                    direction: input.direction,
                    visibility: input.visibility,
                }),
            });
            return channel;
        },
        async deleteChannel(workspaceId, channelId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}`, {
                method: "DELETE",
            });
        },
        async updateChannelDescription(workspaceId, channelId, description) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}`, {
                method: "PATCH",
                body: JSON.stringify({ description }),
            });
        },
        async updateChannelName(workspaceId, channelId, name) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}`, {
                method: "PATCH",
                body: JSON.stringify({ name }),
            });
        },
        async getChannelSubscribers(workspaceId, channelId) {
            const { agents } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/subscribers`);
            return Array.isArray(agents) ? agents : [];
        },
        async setChannelSubscribers(workspaceId, channelId, agentIds) {
            const { agents } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/subscribers`, {
                method: "PUT",
                body: JSON.stringify({ agent_ids: agentIds }),
            });
            return Array.isArray(agents) ? agents : [];
        },
        async markChannelRead(workspaceId, channelId, payload) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/read`, {
                method: "POST",
                body: JSON.stringify(payload ?? {}),
            });
        },
        // --- Agents ---
        async listAgents(workspaceId) {
            const { agents } = await apiFetch(options, `/api/workspaces/${workspaceId}/agents`);
            return agents;
        },
        async createAgent(input) {
            const { agent } = await apiFetch(options, `/api/workspaces/${input.workspace_id}/agents`, {
                method: "POST",
                body: JSON.stringify({
                    name: input.name,
                    working_dir: input.working_dir,
                    system_prompt: input.system_prompt ?? null,
                    model: input.model ?? null,
                    dangerously_skip_permissions: input.dangerously_skip_permissions ?? false,
                    continue_session: input.continue_session ?? false,
                    is_editor: input.is_editor ?? false,
                    host_id: input.host_id ?? null,
                }),
            });
            return agent;
        },
        async startAgent(workspaceId, agentId, opts) {
            const { result } = await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/start`, {
                method: "POST",
                body: JSON.stringify({ continue: opts?.continue ?? false }),
            });
            return result;
        },
        async stopAgent(workspaceId, agentId) {
            const { result } = await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/stop`, { method: "POST" });
            return result;
        },
        async listAgentPresence(workspaceId) {
            const { agents } = await apiFetch(options, `/api/workspaces/${workspaceId}/agents/presence`);
            return agents ?? {};
        },
        // --- Agent ↔ channel subscriptions ---
        async listAgentSubscriptions(workspaceId, agentId) {
            const { channels } = await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/subscriptions`);
            return Array.isArray(channels) ? channels : [];
        },
        async subscribeAgent(workspaceId, agentId, channelId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/subscriptions`, { method: "POST", body: JSON.stringify({ channel_id: channelId }) });
        },
        async unsubscribeAgent(workspaceId, agentId, channelId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/subscriptions/${encodeURIComponent(channelId)}`, { method: "DELETE" });
        },
        // --- Filesystem probe ---
        async listHostFs(hostId, path) {
            return apiFetch(options, `/api/hosts/${hostId}/fs/list`, {
                method: "POST",
                body: JSON.stringify({ path }),
            });
        },
        // --- MCP ---
        async listMcpServers(workspaceId) {
            const { mcp_servers } = await apiFetch(options, `/api/workspaces/${workspaceId}/mcp-servers`);
            return Array.isArray(mcp_servers) ? mcp_servers : [];
        },
        async getMcpJsonConfig(scope, scopeId) {
            const res = await apiFetch(options, `/api/mcp-configs/${scope}/${scopeId}`);
            return res?.config ?? { mcpServers: {} };
        },
        async setMcpJsonConfig(scope, scopeId, config) {
            await apiFetch(options, `/api/mcp-configs/${scope}/${scopeId}`, {
                method: "PUT",
                body: JSON.stringify(config),
            });
        },
        // --- KTK-242: workspace Brave Search API key ---
        async getBraveSearchApiKey(workspaceId) {
            // Hub's masked GET returns `{ configured: bool, masked: string | null }`
            // — never the raw key. The settings UI shows the masked preview.
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/brave-key`);
            return {
                configured: res?.configured ?? false,
                masked: res?.masked ?? null,
            };
        },
        async setBraveSearchApiKey(workspaceId, key) {
            // Empty string clears the key (Hub treats as DELETE-equivalent and
            // fans agent_set_brave_search_api_key with "" so live workers
            // reset to "no provider configured" on the next turn). Body shape
            // is `{ brave_search_api_key: string }` — Hub destructures that
            // key directly (mirrors the column name); sending `{key}` makes
            // it undefined on the server and D1 500s on the bind.
            await apiFetch(options, `/api/workspaces/${workspaceId}/brave-key`, {
                method: "PUT",
                body: JSON.stringify({ brave_search_api_key: key }),
            });
        },
        // --- KTK-246: workspace OpenRouter API key ---
        async getOpenRouterApiKey(workspaceId) {
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/openrouter-key`);
            return {
                configured: res?.configured ?? false,
                masked: res?.masked ?? null,
            };
        },
        async setOpenRouterApiKey(workspaceId, key) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/openrouter-key`, {
                method: "PUT",
                body: JSON.stringify({ openrouter_api_key: key }),
            });
        },
        // --- Decisions (KTK-191) ---
        async listDecisions(workspaceId, status) {
            const qs = status ? `?status=${encodeURIComponent(status)}` : "";
            const { decisions } = await apiFetch(options, `/api/workspaces/${workspaceId}/decisions${qs}`);
            return Array.isArray(decisions) ? decisions : [];
        },
        async getDecision(decisionId) {
            const { decision } = await apiFetch(options, `/api/decisions/${decisionId}`);
            return decision;
        },
        async answerDecision(decisionId, answers) {
            const { decision } = await apiFetch(options, `/api/decisions/${decisionId}/answer`, { method: "POST", body: JSON.stringify({ answers }) });
            return decision;
        },
        async cancelDecision(decisionId) {
            await apiFetch(options, `/api/decisions/${decisionId}`, {
                method: "DELETE",
            });
        },
        // --- Messages ---
        async listMessages(workspaceId, channelId, opts = {}) {
            const params = new URLSearchParams();
            params.set("limit", String(opts.limit ?? 50));
            if (opts.before)
                params.set("before", opts.before);
            const { messages } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages?${params}`);
            return messages;
        },
        async sendMessage(workspaceId, channelId, input) {
            const { message } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages`, {
                method: "POST",
                body: JSON.stringify({
                    id: input.messageId,
                    content: input.content,
                    author_type: input.authorType ?? "human",
                    author_id: input.authorId ?? null,
                    author_name: input.authorName,
                    metadata: input.metadata,
                    thread_id: input.thread_id,
                }),
            });
            return message;
        },
        async updateMessage(workspaceId, channelId, messageId, content) {
            const { message } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}`, {
                method: "PATCH",
                body: JSON.stringify({ content }),
            });
            return message;
        },
        async deleteMessage(workspaceId, channelId, messageId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}`, { method: "DELETE" });
        },
        // --- KTK-385 Threads ---
        async createThread(workspaceId, channelId, messageId, title) {
            const { thread } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}/thread`, { method: "POST", body: JSON.stringify({ title }) });
            return thread;
        },
        async listThreads(workspaceId, channelId) {
            const { threads } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/threads`);
            return Array.isArray(threads) ? threads : [];
        },
        async listWorkspaceThreads(workspaceId) {
            const { threads } = await apiFetch(options, `/api/workspaces/${workspaceId}/threads`);
            return Array.isArray(threads) ? threads : [];
        },
        async resolveThread(workspaceId, channelId, threadId) {
            const { thread } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/threads/${threadId}/resolve`, { method: "POST" });
            return thread;
        },
        async unresolveThread(workspaceId, channelId, threadId) {
            const { thread } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/threads/${threadId}/resolve`, { method: "DELETE" });
            return thread;
        },
        async listThreadMessages(workspaceId, channelId, threadId) {
            const { messages } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/threads/${threadId}/messages`);
            return Array.isArray(messages) ? messages : [];
        },
        // --- Reactions ---
        async getMessageReactions(messageId) {
            const { reactions } = await apiFetch(options, `/api/reactions/message/${messageId}`);
            return reactions;
        },
        async getChannelReactions(channelId) {
            const res = await apiFetch(options, `/api/reactions/channel/${channelId}`);
            return res?.reactions ?? {};
        },
        async addReaction(messageId, emoji, authorName, channelId, authorId = null) {
            const { reaction } = await apiFetch(options, `/api/reactions`, {
                method: "POST",
                body: JSON.stringify({
                    message_id: messageId,
                    emoji,
                    author_type: "human",
                    author_id: authorId,
                    author_name: authorName,
                    channel_id: channelId,
                }),
            });
            return reaction;
        },
        async removeReaction(messageId, emoji, authorName, channelId) {
            await apiFetch(options, `/api/reactions`, {
                method: "DELETE",
                body: JSON.stringify({
                    message_id: messageId,
                    emoji,
                    author_name: authorName,
                    channel_id: channelId,
                }),
            });
        },
        // --- Pinned ---
        async pinMessage(workspaceId, channelId, messageId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}/pin`, { method: "POST" });
        },
        async unpinMessage(workspaceId, channelId, messageId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}/pin`, { method: "DELETE" });
        },
        async listPinnedMessages(workspaceId, channelId) {
            const { messages } = await apiFetch(options, `/api/workspaces/${workspaceId}/channels/${channelId}/pinned`);
            return Array.isArray(messages) ? messages : [];
        },
        // --- Images ---
        async uploadImage(workspaceId, base64Data, mimeType) {
            // Server mounts the upload handler at POST /images/upload (not root),
            // and expects { data, mimeType } as the body — don't snake_case here.
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/images/upload`, {
                method: "POST",
                body: JSON.stringify({ data: base64Data, mimeType }),
            });
            return { url: res.url };
        },
        async setWorkspaceIcon(workspaceId, base64Data, mimeType) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/icon`, {
                method: "PUT",
                body: JSON.stringify({ data: base64Data, mimeType }),
            });
        },
        async deleteWorkspaceIcon(workspaceId) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/icon`, {
                method: "DELETE",
            });
        },
        async authedImageUrl(url) {
            // Already-absolute + non-api sources (data URIs, blob URLs, external
            // CDN links) don't need tokens.
            if (/^(data:|blob:|https?:\/\/)/.test(url))
                return url;
            const token = await options.getToken();
            const absolute = `${options.baseUrl.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
            if (!token)
                return absolute;
            const sep = absolute.includes("?") ? "&" : "?";
            return `${absolute}${sep}token=${encodeURIComponent(token)}`;
        },
        // --- Journalist ---
        async getJournalistStatus(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/status`);
        },
        async updateJournalistSettings(workspaceId, settings) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/settings`, { method: "PATCH", body: JSON.stringify({ settings }) });
        },
        async getJournalistPrompts(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts`);
        },
        async updateJournalistPrompt(workspaceId, kind, prompt) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts/${kind}`, { method: "PUT", body: JSON.stringify({ prompt }) });
        },
        async getJournalistPromptTemplates(workspaceId) {
            // Backend wraps the response as `{ templates, keys }`; the hub
            // client surface returns just the keyed map for ergonomics.
            const res = await apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts`);
            return res?.templates ?? {};
        },
        async putJournalistPromptTemplate(workspaceId, key, value) {
            // Backend keys are dotted (`shared.methodology`); the route uses
            // them verbatim, so encodeURIComponent preserves the dots without
            // double-encoding the slash that would otherwise come from a
            // path split.
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) });
        },
        async previewJournalistPrompt(workspaceId, role) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts/preview?role=${encodeURIComponent(role)}`);
        },
        async resetJournalistPromptTemplate(workspaceId, key) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/prompts/${encodeURIComponent(key)}/reset`, { method: "POST" });
        },
        async triggerJournalist(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/trigger`, { method: "POST" });
        },
        async triggerJournalistDigest(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/trigger/digest`, { method: "POST" });
        },
        async triggerJournalistSynthesis(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/trigger/synthesis`, { method: "POST" });
        },
        async runJournalistPassExtraction(workspaceId, params) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/passes/extraction`, { method: "POST", body: JSON.stringify(params) });
        },
        async runJournalistPassDigest(workspaceId, params) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/passes/digest`, { method: "POST", body: JSON.stringify(params) });
        },
        async runJournalistPassSynthesis(workspaceId, params) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/passes/synthesis`, { method: "POST", body: JSON.stringify(params) });
        },
        async getJournalistLastResults(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/last-results`);
        },
        async generateBriefing(workspaceId, agentId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/briefing`, { method: "POST", body: JSON.stringify({ agent_id: agentId }) });
        },
        async knowledgeQuery(workspaceId, query, agentName) {
            // Backend's /query accepts an optional `agent_name` to flavor the
            // prompt; from the workspace-settings UI we leave it null since the
            // human (Akari) is asking, not a specific agent persona.
            const body = { query };
            if (agentName)
                body.agent_name = agentName;
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/query`, { method: "POST", body: JSON.stringify(body) });
        },
        // --- Vault ---
        async listVaultFiles(workspaceId) {
            const { files } = await apiFetch(options, `/api/workspaces/${workspaceId}/vault/files`);
            return Array.isArray(files) ? files : [];
        },
        async getVaultFile(workspaceId, path) {
            // Hub's wildcard route at vault.ts:92 URL-decodes a single path
            // segment into the full slash-delimited file path. Path segments
            // get encodeURIComponent so embedded slashes don't collide with
            // the route delimiter.
            const encoded = path.split("/").map(encodeURIComponent).join("/");
            return apiFetch(options, `/api/workspaces/${workspaceId}/vault/files/${encoded}`);
        },
        async exportVault(workspaceId) {
            return apiFetchBlob(options, `/api/workspaces/${workspaceId}/journalist/vault/export`);
        },
        async importVault(workspaceId, zip) {
            // Multipart upload: the Worker route reads `file` from the form
            // body. Don't set Content-Type — the browser sets it (with the
            // multipart boundary) when the body is FormData.
            const token = await options.getToken();
            const headers = {};
            if (token)
                headers.Authorization = `Bearer ${token}`;
            const form = new FormData();
            form.append("file", zip, "vault.zip");
            const res = await fetch(`${options.baseUrl}/api/workspaces/${workspaceId}/journalist/vault/import`, { method: "POST", headers, body: form });
            if (!res.ok) {
                const body = await res.text().catch(() => "");
                throw new Error(`Hub POST .../journalist/vault/import failed: ${res.status} ${body}`);
            }
            return (await res.json());
        },
        async getVaultScaffold(workspaceId) {
            return apiFetchBlob(options, `/api/workspaces/${workspaceId}/journalist/vault/scaffold`);
        },
        async resetVaultToScaffold(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/journalist/vault/reset`, { method: "POST" });
        },
        async clearVault(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/vault`, { method: "DELETE" });
        },
        // --- PTY hosts ---
        async listPtyHosts() {
            const { hosts } = await apiFetch(options, `/api/hosts`);
            return Array.isArray(hosts) ? hosts : [];
        },
        async revokePtyHost(hostId) {
            await apiFetch(options, `/api/hosts/${hostId}`, { method: "DELETE" });
        },
        async createHostFsDir(hostId, path) {
            return apiFetch(options, `/api/hosts/${hostId}/fs/mkdir`, {
                method: "POST",
                body: JSON.stringify({ path }),
            });
        },
        async deleteAgent(agentId) {
            await apiFetch(options, `/api/agents/${agentId}`, { method: "DELETE" });
        },
        // KTK-395 — composed CLAUDE.md. Composition and hashing are Hub-side so
        // client, runtime and daemon all agree on the exact bytes.
        async getWorkspaceClaudeMd(workspaceId) {
            // `/resolved`, not `/claude-md` — the latter is the single workspace
            // *layer* and answers `{document: …}` with no composed text. The panel
            // needs platform + workspace composed, and composition stays Hub-side so
            // client, runtime and daemon agree on the exact bytes and hash.
            return fetchWorkspaceClaudeMd(workspaceId);
        },
        async setWorkspaceClaudeMd(workspaceId, content) {
            // Write, then re-read the resolved document rather than trusting the
            // write's response shape. The composed text and hash are recomputed
            // Hub-side on save, and this is the call that already guarantees the
            // shape the panel renders.
            await apiFetch(options, `/api/workspaces/${workspaceId}/claude-md`, {
                method: "PUT",
                body: JSON.stringify({ content }),
            });
            return fetchWorkspaceClaudeMd(workspaceId);
        },
        // `/resolved` rather than the bare agent path: it returns the composed
        // document plus the sync state the host reported, which is what every
        // surface here renders.
        async getAgentClaudeMd(workspaceId, agentId) {
            return fetchAgentClaudeMd(workspaceId, agentId);
        },
        async setAgentClaudeMd(workspaceId, agentId, content) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/agents/${agentId}/claude-md`, { method: "PUT", body: JSON.stringify({ content }) });
            return fetchAgentClaudeMd(workspaceId, agentId);
        },
        async resolveAgentClaudeMd(workspaceId, agentId, resolution) {
            // Two genuinely different operations rather than one endpoint with a
            // direction flag: `adopt-local` takes the host's file as the new agent
            // layer, `push` overwrites the host with ours. Keeping them separate
            // Hub-side means neither can be reached by getting a boolean backwards.
            const path = resolution === "keep_local"
                ? `/api/workspaces/${workspaceId}/agents/${agentId}/claude-md/adopt-local`
                : `/api/workspaces/${workspaceId}/agents/${agentId}/claude-md/push`;
            // `force` only means anything on the push path; the runtime refuses to
            // clobber a drifted file without it, which is the whole point of the
            // never-auto-resolve rule.
            await apiFetch(options, path, {
                method: "POST",
                body: JSON.stringify(resolution === "take_kritaka" ? { force: true } : {}),
            });
            return fetchAgentClaudeMd(workspaceId, agentId);
        },
        async setAgentSystemPrompt(agentId, payload) {
            const { agent } = await apiFetch(options, `/api/agents/${agentId}/system-prompt`, {
                method: "PATCH",
                body: JSON.stringify(payload),
            });
            return agent;
        },
        async setAgentModel(agentId, modelId) {
            const { agent } = await apiFetch(options, `/api/agents/${agentId}/model`, {
                method: "PATCH",
                body: JSON.stringify({ model_id: modelId }),
            });
            return agent;
        },
        async startAgentRuntime(agentId) {
            await apiFetch(options, `/api/agents/${agentId}/start`, { method: "POST" });
        },
        async stopAgentRuntime(agentId) {
            await apiFetch(options, `/api/agents/${agentId}/stop`, { method: "POST" });
        },
        // --- KTK-212 / KTK-218 Agent V2 endpoints ---
        async listAgentRuntimeHosts() {
            const { hosts } = await apiFetch(options, `/api/agent-runtime-hosts`);
            return (Array.isArray(hosts) ? hosts : []);
        },
        async listRuntimeHostFs(hostId, path, workspaceId) {
            return apiFetch(options, `/api/agent-runtime-hosts/${hostId}/fs/list`, {
                method: "POST",
                body: JSON.stringify({ path, workspace_id: workspaceId }),
            });
        },
        async createRuntimeHostFsDir(hostId, path, workspaceId) {
            return apiFetch(options, `/api/agent-runtime-hosts/${hostId}/fs/mkdir`, {
                method: "POST",
                body: JSON.stringify({ path, workspace_id: workspaceId }),
            });
        },
        async createAgentV2(input) {
            const { agent } = await apiFetch(options, `/api/agents`, {
                method: "POST",
                body: JSON.stringify({ ...input, runtime_kind: "agent-v2" }),
            });
            return agent;
        },
        async getWorkspaceUsage(workspaceId, period) {
            // KTK-386 — rolling windows go as from/to; enum periods unchanged.
            const query = typeof period === "string"
                ? `period=${encodeURIComponent(period)}`
                : `from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`;
            return apiFetch(options, `/api/usage/workspace/${workspaceId}?${query}`);
        },
        async getHostUsage(hostId, period) {
            return apiFetch(options, `/api/usage/host/${hostId}?period=${encodeURIComponent(period)}`);
        },
        async getAgentUsage(agentId, period) {
            return apiFetch(options, `/api/usage/agent/${agentId}?period=${encodeURIComponent(period)}`);
        },
        async getWorkspaceSpendCap(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/spend-cap`);
        },
        async setWorkspaceSpendCap(workspaceId, payload) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/spend-cap`, {
                method: "PUT",
                body: JSON.stringify(payload),
            });
        },
        // --- Device-flow registration ---
        async getDeviceRegistrationByCode(userCode) {
            return apiFetch(options, `/api/device-registration/by-code/${encodeURIComponent(userCode)}`);
        },
        async approveDeviceRegistration(userCode) {
            // KTK-253: Hub no longer requires (or returns) `workspace_id` —
            // device registrations are user-scoped post-rocky's migration.
            // Daemon's WSS bus opens connections to every workspace the
            // user is in via `connectHub`'s ownership lookup, so the
            // notion of a "primary workspace" at pair time is dead.
            await apiFetch(options, `/api/device-registration/approve`, {
                method: "POST",
                body: JSON.stringify({ user_code: userCode }),
            });
        },
        async denyDeviceRegistration(userCode) {
            await apiFetch(options, `/api/device-registration/deny`, {
                method: "POST",
                body: JSON.stringify({ user_code: userCode }),
            });
        },
        // --- Webhook routes ---
        async listWebhookRoutes(workspaceId) {
            const { routes } = await apiFetch(options, `/api/workspaces/${workspaceId}/webhooks`);
            return Array.isArray(routes) ? routes : [];
        },
        async createWebhookRoute(workspaceId, routeName, targetChannel, format, signingSecret) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/webhooks`, {
                method: "POST",
                body: JSON.stringify({
                    route_name: routeName,
                    target_channel: targetChannel,
                    format: format ?? "raw",
                    signing_secret: signingSecret ?? null,
                }),
            });
        },
        async deleteWebhookRoute(workspaceId, routeName) {
            await apiFetch(options, `/api/workspaces/${workspaceId}/webhooks/${encodeURIComponent(routeName)}`, { method: "DELETE" });
        },
        // --- Log settings ---
        async getLogSettings(workspaceId) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/log-settings`);
        },
        async updateLogSettings(workspaceId, settings) {
            return apiFetch(options, `/api/workspaces/${workspaceId}/log-settings`, { method: "PATCH", body: JSON.stringify({ settings }) });
        },
        // --- Scout viewer ---
        async listWorkspaceScouts(workspaceId) {
            const { scouts } = await apiFetch(options, `/api/workspaces/${workspaceId}/scouts`);
            return Array.isArray(scouts) ? scouts : [];
        },
        async getViewerIceServers() {
            const { ice_servers } = await apiFetch(options, `/api/scout/stream/viewer-ice-servers`);
            return Array.isArray(ice_servers) ? ice_servers : [];
        },
        async postViewerInterest(instanceId, clientSessionId) {
            const res = await apiFetch(options, `/api/scout/signals`, {
                method: "POST",
                body: JSON.stringify({
                    kind: "viewer_interest",
                    instance_id: instanceId,
                    payload: { client_session_id: clientSessionId },
                }),
            });
            return { active_session_id: res.active_session_id ?? null };
        },
        async joinScoutStream(sessionId, sdpOffer) {
            const res = await apiFetch(options, `/api/scout/stream/${sessionId}/viewers`, {
                method: "POST",
                body: JSON.stringify({ sdp_offer: sdpOffer }),
            });
            let answer = null;
            if (typeof res.sdp_answer === "string")
                answer = res.sdp_answer;
            else if (res.sdp_answer && typeof res.sdp_answer === "object") {
                answer = res.sdp_answer.sdp ?? null;
            }
            return { viewer_id: res.viewer_id, sdp_answer: answer };
        },
        async leaveScoutStream(sessionId, viewerId) {
            await apiFetch(options, `/api/scout/stream/${sessionId}/viewers/${viewerId}`, { method: "DELETE" });
        },
        // --- Scout presence + orchestration ---
        async joinScoutPresence(instanceId) {
            await apiFetch(options, `/api/scout/presence/${instanceId}/join`, {
                method: "POST",
                body: "{}",
            });
        },
        async leaveScoutPresence(instanceId) {
            await apiFetch(options, `/api/scout/presence/${instanceId}/leave`, {
                method: "POST",
                body: "{}",
            });
        },
        async listScoutPresence(instanceId) {
            const { members } = await apiFetch(options, `/api/scout/presence/${instanceId}/members`);
            return Array.isArray(members) ? members : [];
        },
        async postScoutStopGoal(instanceId, goalId) {
            await apiFetch(options, `/api/scout/signals`, {
                method: "POST",
                body: JSON.stringify({
                    kind: "scout_stop_goal",
                    instance_id: instanceId,
                    payload: { goal_id: goalId },
                }),
            });
        },
    };
}
//# sourceMappingURL=browser-hub-client.js.map